import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { IconSearchTool } from '../../tools/IconSearchTool';
import { SetIconTool } from '../../tools/SetIconTool';
import type { LintFinding } from '../videoLinter';

import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

/**
 * Recursively collects a map of asset name -> default icon url from a raw
 * scene design config. Mirrors the walk in placeholderText: the config is an
 * opaque nested structure, so we look for the `{ name, sourceData: { url } }`
 * shape rather than assuming a fixed tree layout.
 */
function collectIconUrlByName(node: unknown, map: Map<string, string>): void {
    if (!node || typeof node !== 'object') {
        return;
    }
    if (Array.isArray(node)) {
        node.forEach((child) => collectIconUrlByName(child, map));
        return;
    }
    const obj = node as Record<string, unknown>;
    const sourceData = obj.sourceData as Record<string, unknown> | undefined;
    if (
        typeof obj.name === 'string' &&
        sourceData &&
        typeof sourceData === 'object' &&
        typeof sourceData.url === 'string'
    ) {
        map.set(obj.name, sourceData.url);
    }
    Object.values(obj).forEach((value) => {
        if (value && typeof value === 'object') {
            collectIconUrlByName(value, map);
        }
    });
}

function getSceneDesignDefaultIconUrls(scene: Scene): Map<string, string> | null {
    try {
        const root = scene.root as unknown as {
            _getSceneDesignConfig?: (sceneDesignId: string, aspectRatio: string) => unknown;
            aspectRatio: string;
        };
        if (typeof root._getSceneDesignConfig !== 'function') {
            return null;
        }
        const config = root._getSceneDesignConfig(scene.sceneDesign, root.aspectRatio);
        const map = new Map<string, string>();
        collectIconUrlByName(config, map);
        return map;
    } catch {
        return null;
    }
}

interface IconAssetLike {
    name: string;
    isEditable: boolean;
    sourceData?: { url?: string };
}

/**
 * Warning: an icon asset still shows the scene design's default icon. Like
 * unchanged placeholder text, a default icon reads as an unfinished template —
 * every icon should be swapped for one relevant to the scene's content.
 */
export function checkPlaceholderIcon(luminary: Luminary, _getState: GetRootState): LintFinding[] {
    const findings: LintFinding[] = [];

    luminary.scenes.forEach((scene) => {
        const defaults = getSceneDesignDefaultIconUrls(scene);
        if (!defaults || defaults.size === 0) {
            return;
        }
        const iconAssets = scene.getAssetsByType(
            VideoData.ICON_ASSET_TYPE,
        ) as unknown as IconAssetLike[];
        iconAssets
            .filter((asset) => asset.isEditable)
            .forEach((asset) => {
                const defaultUrl = defaults.get(asset.name);
                const currentUrl = asset.sourceData?.url;
                if (defaultUrl && currentUrl && defaultUrl === currentUrl) {
                    findings.push({
                        check: 'placeholder_icon',
                        severity: 'warning',
                        sceneId: scene.id,
                        message: `Icon asset "${asset.name}" still shows the design's default icon. Replace it with an icon relevant to this scene's content (${IconSearchTool.toolName}, then ${SetIconTool.toolName}) — a default icon reads as an unfinished placeholder.`,
                    });
                }
            });
    });

    return findings;
}
